import type { TableAction, BulkAction } from '@shared/types/data-table'
import type { Product } from '@shared/types/products'
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"
import { Edit2, Trash2 } from 'lucide-react'

interface ProductSpecificHandlers {
  onView?: (product: Product) => void
  onViewProfile?: (product: Product) => void
  onDuplicate?: (product: Product) => void
  onUpdateStatus?: (product: Product, status: string) => void
  onBulkUpdateStatus?: (productIds: string[], status: string) => void
  onBulkDelete?: (productIds: string[]) => void
}

export type ProductActionHandlers = BaseActionHandlers<Product> & ProductSpecificHandlers

// Row actions for tables - quick access operations
const createProductRowActions = (handlers: ProductActionHandlers) => [
  {
    key: "view",
    label: "View Details",
    onClick: (product: Product) => handlers.onView?.(product),
  },
  {
    key: "viewProfile",
    label: "View Profile",
    onClick: (product: Product) => handlers.onViewProfile?.(product),
  },
  {
    key: "duplicate",
    label: "Duplicate",
    onClick: (product: Product) => handlers.onDuplicate?.(product),
  },
] satisfies TableAction<Product>[]

// Panel actions for detail pages - comprehensive operations
const createProductPanelActions = (handlers: ProductActionHandlers) => [
  {
    key: "duplicate",
    label: "Duplicate",
    onClick: (product: Product) => handlers.onDuplicate?.(product),
  },
  {
    key: "active",
    label: "Mark as Active",
    onClick: (product: Product) => handlers.onUpdateStatus?.(product, 'active'),
    condition: (product: Product) => product.status !== 'active',
  },
  {
    key: "draft",
    label: "Mark as Draft",
    onClick: (product: Product) => handlers.onUpdateStatus?.(product, 'draft'),
    condition: (product: Product) => product.status !== 'draft',
  },
  {
    key: "archive",
    label: "Archive",
    onClick: (product: Product) => handlers.onUpdateStatus?.(product, 'archived'),
    condition: (product: Product) => product.status !== 'archived',
  },
] satisfies TableAction<Product>[]

export const getProductActions = (handlers: ProductActionHandlers = {}) => {
  const baseActions = createBaseActions('Product', handlers, {
    showEdit: true,
    showDelete: true,
    showFlag: false,
    showAddNote: false,
    showCopyId: true,
    showExport: true,
  })

  const productSpecificActions = {
    bulkActions: [
      {
        key: "updateStatus",
        label: "Update Status",
        icon: <Edit2 className="h-4 w-4" />,
        onClick: (ids: string[]) => handlers.onBulkUpdateStatus?.(ids, 'active'),
        disabled: (ids: string[]) => ids.length === 0,
      },
      {
        key: "delete",
        label: "Delete Products",
        icon: <Trash2 className="h-4 w-4" />,
        onClick: (ids: string[]) => handlers.onBulkDelete?.(ids),
        disabled: (ids: string[]) => ids.length === 0,
        variant: "destructive" as const,
      },
    ] satisfies BulkAction[],
    rowActions: createProductRowActions(handlers),
  }

  return mergeActions(baseActions, productSpecificActions)
}

export const getProductBulkActions = (handlers: ProductActionHandlers = {}): BulkAction[] =>
  getProductActions(handlers).bulkActions

export const getProductRowActions = (handlers: ProductActionHandlers = {}): TableAction<Product>[] =>
  getProductActions(handlers).rowActions

export const getProductPanelActions = (handlers: ProductActionHandlers = {}): TableAction<Product>[] => {
  const baseActions = createBaseActions('Product', handlers, {
    showEdit: true,
    showDelete: true,
    showFlag: true,
    showAddNote: true,
    showCopyId: true,
    showExport: true,
  })

  return mergeActions(baseActions, {
    rowActions: createProductPanelActions(handlers),
  }).rowActions
}

export const getProductHoverActions = (): TableAction<Product>[] => []
